import { useEffect, useMemo, useState } from "react"
import { supabase } from "../../supabaseClient"
import { ArrowLeft, Pencil, Trash2 } from "lucide-react"
import EditSessionModal from "./EditSessionModal"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"

/* -----------------------------------
   Types
----------------------------------- */
interface SessionRow {
  id: string
  task_id: string | null
  start_time: string
  end_time: string | null
  duration_mins: number | null
  is_manually_edited: boolean
}

interface BreakRow {
  session_id: string
  start_time: string
  end_time: string | null
}

interface Job {
  id: string
  task_name: string
  hourly_rate: number
  is_active: boolean
}

type RowVM = SessionRow & {
  jobName: string
  breakMinutes: number
  earnings: number
}

type GroupBy = "none" | "day" | "week" | "month"

type Totals = { earnings: number; minutes: number }

/* -----------------------------------
   Helpers
----------------------------------- */
function sumOf(rows: RowVM[]): Totals {
  return rows.reduce(
    (acc, r) => ({
      earnings: acc.earnings + r.earnings,
      minutes: acc.minutes + (r.duration_mins || 0),
    }),
    { earnings: 0, minutes: 0 }
  )
}

function startOfDay(d: Date) {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

function startOfWeek(d: Date) {
  // Monday-start week
  const x = startOfDay(d)
  const day = x.getDay() // 0=Sun..6=Sat
  const diff = day === 0 ? -6 : 1 - day
  x.setDate(x.getDate() + diff)
  return x
}

function startOfMonth(d: Date) {
  const x = new Date(d)
  x.setDate(1)
  x.setHours(0, 0, 0, 0)
  return x
}

function groupKey(iso: string, mode: GroupBy) {
  const d = new Date(iso)
  if (mode === "day") return startOfDay(d).toISOString()
  if (mode === "week") return startOfWeek(d).toISOString()
  if (mode === "month") return startOfMonth(d).toISOString()
  return "all"
}

function groupLabel(key: string, mode: GroupBy) {
  if (mode === "none") return ""
  const d = new Date(key)
  if (mode === "day") return d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })
  if (mode === "week") return `Week of ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" })
}

/* -----------------------------------
   Component
----------------------------------- */
interface SessionHistoryProps {
  refreshKey?: number
  initialJobFilter?: string
  onBack?: () => void
}

export default function SessionHistory({ refreshKey, initialJobFilter, onBack }: SessionHistoryProps) {
  const [sessions, setSessions] = useState<SessionRow[]>([])
  const [breaks, setBreaks] = useState<BreakRow[]>([])
  const [jobs, setJobs] = useState<Job[]>([])
  const [loading, setLoading] = useState(true)

  // Filters
  const [jobFilter, setJobFilter] = useState(initialJobFilter || "all")
  const [fromDate, setFromDate] = useState("")
  const [toDate, setToDate] = useState("")

  // Grouping + selection
  const [groupBy, setGroupBy] = useState<GroupBy>("none")
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // Edit modal
  const [editOpen, setEditOpen] = useState(false)
  const [editSessionId, setEditSessionId] = useState<string | null>(null)

  /* -----------------------------------
     Load data
  ----------------------------------- */
  const loadData = async () => {
    setLoading(true)

    const [{ data: s }, { data: b }, { data: j }] = await Promise.all([
      supabase
        .from("work_sessions")
        .select(
          "id, task_id, start_time, end_time, duration_mins, is_manually_edited"
        )
        .eq("status", "completed")
        .order("start_time", { ascending: false }),

      supabase
        .from("work_breaks")
        .select("session_id, start_time, end_time"),

      // Includes inactive (soft-deleted) jobs so past sessions still resolve
      // their name/rate correctly.
      supabase
        .from("work_tasks")
        .select("id, task_name, hourly_rate, is_active")
        .order("task_name"),
    ])

    setSessions(s || [])
    setBreaks(b || [])
    setJobs(j || [])
    setLoading(false)
  }

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey])

  // Clear selection whenever the visible set of rows changes via filters
  useEffect(() => {
    setSelectedIds(new Set())
  }, [jobFilter, fromDate, toDate])

  /* -----------------------------------
     Build rows (filtered + computed)
  ----------------------------------- */
  const rows: RowVM[] = useMemo(() => {
    return sessions
      .filter((s) => {
        if (jobFilter !== "all" && s.task_id !== jobFilter) return false
        if (fromDate && new Date(s.start_time) < new Date(fromDate)) return false
        if (toDate && new Date(s.start_time) > new Date(toDate)) return false
        return true
      })
      .map((s) => {
        const job = jobs.find((j) => j.id === s.task_id)

        const sessionBreaks = breaks.filter(
          (b) => b.session_id === s.id && b.end_time
        )

        const breakMinutes = sessionBreaks.reduce((sum, b) => {
          return (
            sum +
            (new Date(b.end_time!).getTime() -
              new Date(b.start_time).getTime()) /
              60000
          )
        }, 0)

        const workedMinutes = s.duration_mins || 0
        const earnings =
          job && workedMinutes
            ? (workedMinutes / 60) * job.hourly_rate
            : 0

        return {
          ...s,
          jobName: job?.task_name || "(Missing job)",
          breakMinutes: Math.round(breakMinutes),
          earnings,
        }
      })
  }, [sessions, breaks, jobs, jobFilter, fromDate, toDate])

  const totalEarned = useMemo(
    () => rows.reduce((sum, r) => sum + r.earnings, 0),
    [rows]
  )

  const totalMinutes = useMemo(
    () => rows.reduce((sum, r) => sum + (r.duration_mins || 0), 0),
    [rows]
  )

  /* -----------------------------------
     Grouping (independent of selection so
     toggling checkboxes never re-groups)
  ----------------------------------- */
  const groups = useMemo(() => {
    if (groupBy === "none") {
      return [{ key: "all", label: "", rows, subtotal: sumOf(rows) }]
    }

    const map = new Map<string, RowVM[]>()
    for (const r of rows) {
      const k = groupKey(r.start_time, groupBy)
      const arr = map.get(k)
      if (arr) arr.push(r)
      else map.set(k, [r])
    }

    return Array.from(map.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([key, groupRows]) => ({
        key,
        label: groupLabel(key, groupBy),
        rows: groupRows,
        subtotal: sumOf(groupRows),
      }))
  }, [rows, groupBy])

  /* -----------------------------------
     Selection
  ----------------------------------- */
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleGroup = (groupRows: RowVM[]) => {
    const allSelected = groupRows.every((r) => selectedIds.has(r.id))
    setSelectedIds((prev) => {
      const next = new Set(prev)
      groupRows.forEach((r) => (allSelected ? next.delete(r.id) : next.add(r.id)))
      return next
    })
  }

  const selectedTotal = useMemo(
    () => sumOf(rows.filter((r) => selectedIds.has(r.id))),
    [rows, selectedIds]
  )

  /* -----------------------------------
     Actions
  ----------------------------------- */
  const deleteSession = async (id: string) => {
    if (!window.confirm("Delete this session?")) return
    await supabase.from("work_sessions").delete().eq("id", id)
    setSelectedIds((prev) => {
      if (!prev.has(id)) return prev
      const next = new Set(prev)
      next.delete(id)
      return next
    })
    loadData()
  }

  /* -----------------------------------
     Export CSV
  ----------------------------------- */
  const exportCSV = () => {
    const header = [
      "Job",
      "Start",
      "End",
      "Worked Minutes",
      "Break Minutes",
      "Earnings",
      "Edited",
    ]

    const lines = rows.map((r) => [
      r.jobName,
      r.start_time,
      r.end_time,
      r.duration_mins,
      r.breakMinutes,
      r.earnings.toFixed(2),
      r.is_manually_edited ? "YES" : "NO",
    ])

    const csv =
      [header, ...lines].map((l) => l.join(",")).join("\n")

    const blob = new Blob([csv], { type: "text/csv" })
    const url = URL.createObjectURL(blob)

    const a = document.createElement("a")
    a.href = url
    a.download = "session-history.csv"
    a.click()
  }

  /* -----------------------------------
     Export PDF (Monthly Report)
  ----------------------------------- */
  const exportPDF = () => {
    if (rows.length === 0) {
      alert("No data to export")
      return
    }

    const doc = new jsPDF()

    const jobName =
      jobFilter === "all"
        ? "All jobs"
        : jobs.find((j) => j.id === jobFilter)?.task_name || "Job"

    const period =
      `${fromDate || "Beginning"} → ${toDate || "Today"}`

    // Header
    doc.setFontSize(18)
    doc.text("uCloud360", 14, 20)

    doc.setFontSize(12)
    doc.text("Time Report", 14, 28)

    doc.setFontSize(10)
    doc.text(`Job: ${jobName}`, 14, 36)
    doc.text(`Period: ${period}`, 14, 42)

    // Summary
    const totalWorkedMins = rows.reduce(
      (sum, r) => sum + (r.duration_mins || 0),
      0
    )

    const totalBreakMins = rows.reduce(
      (sum, r) => sum + r.breakMinutes,
      0
    )

    doc.setFontSize(11)
    doc.text("Summary", 14, 54)

    doc.setFontSize(10)
    doc.text(
      `Worked time: ${(totalWorkedMins / 60).toFixed(2)} hours`,
      14,
      62
    )
    doc.text(`Break time: ${totalBreakMins} minutes`, 14, 68)
    doc.text(`Total earned: $${totalEarned.toFixed(2)}`, 14, 74)

    // Table
    autoTable(doc, {
      startY: 82,
      head: [[
        "Date",
        "Job",
        "Start",
        "End",
        "Worked (min)",
        "Break (min)",
        "Earnings ($)"
      ]],
      body: rows.map((r) => [
        new Date(r.start_time).toLocaleDateString(),
        r.jobName,
        new Date(r.start_time).toLocaleTimeString(),
        r.end_time ? new Date(r.end_time).toLocaleTimeString() : "",
        r.duration_mins || 0,
        r.breakMinutes,
        r.earnings.toFixed(2),
      ]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [0, 0, 0] },
    })

    const pageHeight = doc.internal.pageSize.height
    doc.setFontSize(9)
    doc.text(
      `Generated on ${new Date().toLocaleString()}`,
      14,
      pageHeight - 10
    )

    doc.save("time-report.pdf")
  }

  /* -----------------------------------
     Render
  ----------------------------------- */
  return (
    <div className={selectedIds.size > 0 ? "pb-24" : ""}>
      {onBack && (
        <button
          onClick={onBack}
          className="mb-3 -ml-2 flex items-center gap-1.5 px-2 py-1.5 rounded-xl text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-neutral-800 hover:text-gray-800 dark:hover:text-gray-200 transition"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
      )}

      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold">Session History</h2>
          <p className="text-sm text-gray-500">
            Filter, edit, export or delete sessions.
          </p>
        </div>

        <div className="text-right">
          <p className="text-sm text-gray-500">Total earned</p>
          <p className="text-xl font-semibold">
            ${totalEarned.toFixed(2)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {(totalMinutes / 60).toFixed(2)}h worked
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <select
          value={jobFilter}
          onChange={(e) => setJobFilter(e.target.value)}
          className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-neutral-800 px-3 py-2 text-sm text-black dark:text-white"
        >
          <option value="all">All jobs</option>
          {jobs.map((j) => (
            <option key={j.id} value={j.id}>
              {j.task_name}{!j.is_active ? " (inactive)" : ""}
            </option>
          ))}
        </select>

        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-neutral-800 px-3 py-2 text-sm text-black dark:text-white"
        />

        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-neutral-800 px-3 py-2 text-sm text-black dark:text-white"
        />

        <div className="flex items-center gap-1 rounded-xl border border-gray-200 dark:border-gray-700 p-1">
          {(["none", "day", "week", "month"] as GroupBy[]).map((g) => (
            <button
              key={g}
              onClick={() => setGroupBy(g)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                groupBy === g
                  ? "bg-black text-white dark:bg-white dark:text-black"
                  : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-800"
              }`}
            >
              {g === "none" ? "No grouping" : g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          ))}
        </div>

        {rows.length > 0 && (
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer select-none px-1">
            <input
              type="checkbox"
              checked={rows.length > 0 && rows.every((r) => selectedIds.has(r.id))}
              ref={(el) => {
                if (el) {
                  const allSelected = rows.every((r) => selectedIds.has(r.id))
                  el.indeterminate = rows.some((r) => selectedIds.has(r.id)) && !allSelected
                }
              }}
              onChange={() => toggleGroup(rows)}
              className="h-4 w-4 rounded border-gray-300 text-[#2563EB] focus:ring-[#2563EB]"
            />
            Select all
          </label>
        )}

        <button
          onClick={exportCSV}
          className="ml-auto bg-gray-100 dark:bg-neutral-700 px-4 py-2 rounded-xl text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-neutral-600 transition"
        >
          Export CSV
        </button>

        <button
          onClick={exportPDF}
          className="bg-black text-white px-4 py-2 rounded-lg text-sm"
        >
          Export PDF
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading sessions...</div>
      ) : rows.length === 0 ? (
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-6 text-sm text-gray-500 dark:text-gray-400 bg-white dark:bg-neutral-900">
          No sessions found.
        </div>
      ) : (
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-neutral-900">
          {groups.map((g) => {
            const allSelected = g.rows.length > 0 && g.rows.every((r) => selectedIds.has(r.id))
            const someSelected = g.rows.some((r) => selectedIds.has(r.id))

            return (
              <div key={g.key}>
                {groupBy !== "none" && (
                  <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-gray-50 dark:bg-neutral-800 text-sm border-b border-gray-100 dark:border-gray-800">
                    <label className="flex items-center gap-2 cursor-pointer font-medium">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected && !allSelected
                        }}
                        onChange={() => toggleGroup(g.rows)}
                        className="h-4 w-4 rounded border-gray-300 text-[#2563EB] focus:ring-[#2563EB]"
                      />
                      {g.label}
                    </label>
                    <span className="text-gray-500 dark:text-gray-400">
                      {(g.subtotal.minutes / 60).toFixed(2)}h · ${g.subtotal.earnings.toFixed(2)}
                    </span>
                  </div>
                )}

                {g.rows.map((r) => (
                  <div
                    key={r.id}
                    className="flex flex-col gap-2 sm:grid sm:grid-cols-[24px_1fr_auto_auto] sm:items-center sm:gap-x-4 py-3 px-4 border-b border-gray-100 dark:border-gray-800 last:border-b-0"
                  >
                    <div className="flex items-start gap-3 sm:contents">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(r.id)}
                        onChange={() => toggleSelect(r.id)}
                        className="mt-0.5 sm:mt-0 h-5 w-5 shrink-0 rounded border-gray-300 text-[#2563EB] focus:ring-[#2563EB]"
                      />
                      <div className="min-w-0 flex-1 sm:flex-none">
                        <p className="font-medium text-sm truncate">{r.jobName}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {new Date(r.start_time).toLocaleDateString()} ·{" "}
                          {new Date(r.start_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          {" – "}
                          {r.end_time
                            ? new Date(r.end_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                            : "—"}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {r.duration_mins ?? 0}m worked · {r.breakMinutes}m break
                          {r.is_manually_edited ? " · edited" : ""}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between sm:contents">
                      <span className="text-sm font-semibold sm:text-right">
                        ${r.earnings.toFixed(2)}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            setEditSessionId(r.id)
                            setEditOpen(true)
                          }}
                          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 text-gray-600 dark:text-gray-300 transition"
                          title="Edit session"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteSession(r.id)}
                          className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600 transition"
                          title="Delete session"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      )}

      {/* Floating selection total bar */}
      {selectedIds.size > 0 && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 bg-black text-white dark:bg-white dark:text-black shadow-[0_-2px_10px_rgba(0,0,0,0.15)]"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-between">
            <div className="text-sm">
              <span className="font-semibold">{selectedIds.size}</span> selected —{" "}
              {(selectedTotal.minutes / 60).toFixed(2)}h ·{" "}
              <span className="font-semibold">${selectedTotal.earnings.toFixed(2)}</span>
            </div>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-sm underline hover:opacity-80 transition"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      <EditSessionModal
        isOpen={editOpen}
        sessionId={editSessionId}
        onClose={() => {
          setEditOpen(false)
          setEditSessionId(null)
        }}
        onSaved={loadData}
      />
    </div>
  )
}
